export interface ClientProfileData {
  client_id: string;
  client_name: string;
  client_email: string;
  stripe_id: string;
  seller_ids: unknown;
}

const isRecord = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const asText = (value: unknown): string => {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  return String(value).trim();
};

const looksLikeClient = (record: Record<string, any>): boolean =>
  [
    'client_id',
    'client_name',
    'client_email',
    'seller_ids',
    'sellers',
    'stripe_id',
  ].some((key) => record[key] !== undefined && record[key] !== null);

const findClientRecord = (value: unknown, depth = 0): Record<string, any> | null => {
  if (value === null || value === undefined || depth > 5) return null;

  if (Array.isArray(value)) {
    for (const entry of value) {
      const found = findClientRecord(entry, depth + 1);
      if (found) return found;
    }
    return null;
  }

  if (!isRecord(value)) return null;

  if (isRecord(value.client)) {
    const client = findClientRecord(value.client, depth + 1);
    if (client) return client;
  }

  if (looksLikeClient(value)) return value;

  for (const key of ['client', 'data', 'answer', 'result', 'profile', 'client_profile', 'campaigns']) {
    if (value[key] === undefined) continue;
    const found = findClientRecord(value[key], depth + 1);
    if (found) return found;
  }

  return null;
};

export const hasSellerIds = (value: unknown): boolean => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return Boolean(normalized && normalized !== 'null' && normalized !== '[]');
  }
  if (Array.isArray(value)) return value.length > 0;
  if (isRecord(value)) return Object.keys(value).length > 0;
  return value !== null && value !== undefined;
};

export const extractClientProfileData = (
  value: unknown,
  fallbackEmail: string,
): ClientProfileData | null => {
  const client = findClientRecord(value);
  if (!client) return null;

  return {
    client_id: asText(client.client_id ?? client.id ?? client.$id),
    client_name: asText(client.client_name ?? client.name),
    client_email: asText(client.client_email ?? client.email) || fallbackEmail,
    stripe_id: asText(client.stripe_id),
    seller_ids: client.seller_ids ?? client.sellers ?? [],
  };
};
